// Google Search Console integration: client-safe types, constants, and pure
// helpers. Keep this module dependency-free: it is imported by dashboard UI
// components, so nothing server-only (data layer, auth, tokens) may land here.

export type GscConnectionStatus = "pending_property" | "connected" | "error";

/** The only public website profile allowed to expose aggregated GSC trends. */
const PUBLIC_GSC_TRENDS_WEBSITE_SLUG = "verifieddr-com";

export function isPublicGscTrendsWebsite(slug: string): boolean {
	return slug.toLowerCase() === PUBLIC_GSC_TRENDS_WEBSITE_SLUG;
}

/**
 * GSC connection state and its public-display preference are private for every
 * customer profile. Keep the fields only on VerifiedDR's own public listing.
 */
export function redactPublicGscMetadata<
	T extends {
		slug: string;
		gscVerified?: boolean;
		showSearchTrends?: boolean;
	},
>(website: T): Omit<T, "gscVerified" | "showSearchTrends"> | T {
	if (isPublicGscTrendsWebsite(website.slug)) return website;
	const {
		gscVerified: _gscVerified,
		showSearchTrends: _showSearchTrends,
		...rest
	} = website;
	return rest;
}

export type GscProperty = {
	siteUrl: string;
	permissionLevel: string;
};

/** One day of search totals for a site (Search Analytics, dimension=date). */
export type GscDailyPoint = {
	date: string; // YYYY-MM-DD
	clicks: number;
	impressions: number;
	ctr: number;
	position: number;
};

/** One row of a top-N snapshot (query / page / country / device). */
export type GscDimensionRow = {
	key: string;
	clicks: number;
	impressions: number;
	ctr: number;
	position: number;
};

export type GscDimension = "query" | "page" | "country" | "device";

export type GscTotals = {
	clicks: number;
	impressions: number;
	ctr: number;
	position: number | null;
};

type GscConnectionSummary = {
	status: GscConnectionStatus;
	propertyUrl: string | null;
	googleEmail: string | null;
	lastSyncAt: string | null;
	lastSyncError: string | null;
};

export type GscSnapshot = {
	rangeStart: string;
	rangeEnd: string;
	queries: GscDimensionRow[];
	pages: GscDimensionRow[];
	countries: GscDimensionRow[];
	devices: GscDimensionRow[];
};

export type GscDashboardData = {
	/** null when the site has never been connected. */
	connection: GscConnectionSummary | null;
	/** GSC properties on the connected Google account, present only while a
	 * property still needs to be picked (status pending_property). */
	properties?: GscProperty[];
	series: GscDailyPoint[];
	totals: GscTotals | null;
	/** Same-length window immediately before `series`, for period deltas. */
	previousTotals: GscTotals | null;
	snapshot: GscSnapshot | null;
};

export const GSC_DATE_RANGES = [
	{ key: "28d", label: "1 month", days: 28 },
	{ key: "3m", label: "3 months", days: 91 },
	{ key: "6m", label: "6 months", days: 182 },
	{ key: "12m", label: "12 months", days: 365 },
	{ key: "16m", label: "16 months", days: 486 },
] as const;

export type GscDateRangeKey = (typeof GSC_DATE_RANGES)[number]["key"];

export function gscRangeDays(key: string): number {
	return (
		GSC_DATE_RANGES.find((range) => range.key === key)?.days ??
		GSC_DATE_RANGES[0].days
	);
}

/** YYYY-MM-DD in UTC: the format the Search Analytics API speaks. */
export function isoDate(date: Date): string {
	return date.toISOString().slice(0, 10);
}

export function addDays(date: Date, days: number): Date {
	return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
}

export function hostFromUrl(url: string): string {
	try {
		return new URL(url).hostname.toLowerCase().replace(/^www\./, "");
	} catch {
		return url
			.toLowerCase()
			.replace(/^https?:\/\//, "")
			.replace(/^www\./, "")
			.split("/")[0];
	}
}

/** Deep link to a specific property in Google Search Console. */
export function gscPropertyUrl(property: string): string {
	const params = new URLSearchParams({
		utm_source: "about-page",
		resource_id: property,
	});
	return `https://search.google.com/search-console?${params.toString()}`;
}

function urlPrefixHost(siteUrl: string): string | null {
	try {
		return new URL(siteUrl).hostname.toLowerCase().replace(/^www\./, "");
	} catch {
		return null;
	}
}

/**
 * Pick the GSC property that best covers a website URL, or null when none
 * matches. Preference order: domain property of the exact host, domain
 * property of a parent domain (covers subdomain sites), then a URL-prefix
 * property on the same host (https and shorter paths win).
 */
export function pickGscProperty(
	websiteUrl: string,
	properties: GscProperty[],
): string | null {
	const host = hostFromUrl(websiteUrl);
	if (!host) return null;
	const usable = properties.filter(
		(property) => property.permissionLevel !== "siteUnverifiedUser",
	);

	const domainProps = usable
		.filter((property) => property.siteUrl.startsWith("sc-domain:"))
		.map((property) => ({
			siteUrl: property.siteUrl,
			domain: property.siteUrl.slice("sc-domain:".length).toLowerCase(),
		}));
	const exactDomain = domainProps.find((property) => property.domain === host);
	if (exactDomain) return exactDomain.siteUrl;
	const parentDomain = domainProps
		.filter((property) => host.endsWith(`.${property.domain}`))
		// Deepest parent wins: blog.shop.example.com prefers shop.example.com.
		.sort((a, b) => b.domain.length - a.domain.length)[0];
	if (parentDomain) return parentDomain.siteUrl;

	const prefixProps = usable
		.filter((property) => !property.siteUrl.startsWith("sc-domain:"))
		.filter((property) => urlPrefixHost(property.siteUrl) === host)
		.sort((a, b) => {
			const aHttps = a.siteUrl.startsWith("https://") ? 0 : 1;
			const bHttps = b.siteUrl.startsWith("https://") ? 0 : 1;
			return aHttps - bHttps || a.siteUrl.length - b.siteUrl.length;
		});
	return prefixProps[0]?.siteUrl ?? null;
}

/**
 * Aggregate daily points into window totals. CTR is recomputed from the sums
 * (never averaged), and position is impressions-weighted: averaging raw
 * positions would let a 2-impression day count as much as a 20k one.
 */
export function computeGscTotals(series: GscDailyPoint[]): GscTotals | null {
	if (series.length === 0) return null;
	let clicks = 0;
	let impressions = 0;
	let positionWeight = 0;
	for (const point of series) {
		clicks += point.clicks;
		impressions += point.impressions;
		positionWeight += point.position * point.impressions;
	}
	return {
		clicks,
		impressions,
		ctr: impressions > 0 ? clicks / impressions : 0,
		position: impressions > 0 ? positionWeight / impressions : null,
	};
}
